import { useNavigate, Link } from "react-router-dom";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "./AuthContext";
import { useState } from "react";
import { stripeAPI } from "../services/api";
import { buildValidatedBookingReturn } from "../utils/bookingReturnUtils";
import "./CartPage.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CartPage = () => {
  const { token } = useAuth();
  const { cartItems, updateItemQuantity, removeItem, bookingReturn } =
    useShoppingCart();
  const navigate = useNavigate();
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [refundPolicyAgreed, setRefundPolicyAgreed] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleQuantityChange = (itemId, newQuantity) => {
    // Find the item to check if it's an intro offer
    const item = cartItems.find((item) => item.id === itemId);

    // If it's an intro offer, don't update the quantity
    if (item && item.isIntroOffer) {
      return;
    }

    // Otherwise, update as normal
    const quantity = newQuantity < 1 ? 1 : newQuantity;
    updateItemQuantity(itemId, quantity);
  };

  const isIntroOffer = (item) => item.isIntroOffer === true;

  const handleStripeCheckout = async (event) => {
    event.preventDefault();

    // Check if user has agreed to terms and refund policy
    if (!termsAgreed) {
      setShowError(true);
      return;
    }

    if (!token) {
      navigate("/login", { state: { from: "/cart" } });
      return;
    }

    try {
      const stripe = await stripePromise;
      // ADR-027 §A1.4(a): on the past-renewal Path-B legs, additionally pass
      // the booking-return context so the deployed backend conditionally
      // parameterizes success_url and round-trips it across the external
      // Stripe redirect (kills the R-020 legacy browser-storage
      // paid-but-stranded vector). Re-validated to the deployed BE's
      // fail-closed contract at the emit point so a stale/partial context can
      // never produce a BE-rejected payload (which would silently fall back
      // to bare /success and re-strand the paying customer). Same
      // createCheckoutSession call + redirectToCheckout({ sessionId })
      // mechanism — only the body gains one optional field (ADR-027 §A1.3
      // reuse boundary).
      const validatedBookingReturn = bookingReturn
        ? buildValidatedBookingReturn(bookingReturn)
        : null;
      const checkoutBody = { cartItems };
      if (validatedBookingReturn) {
        checkoutBody.bookingReturn = validatedBookingReturn;
      }
      // Use the centralized API service instead of direct axios call
      const response = await stripeAPI.createCheckoutSession(checkoutBody);

      // If the session is created successfully, redirect to checkout
      const { sessionId } = response.data;
      stripe.redirectToCheckout({ sessionId });
    } catch (error) {
      console.error("Error during checkout:", error);
      // Check if error response contains error message and redirect URL
      if (error.response && error.response.data) {
        const errMsg = error.response.data.error;
        const redirectUrl = error.response.data.redirect;
        alert(errMsg);
        if (redirectUrl) {
          // Use window.location.href to force a redirect to the absolute URL
          window.location.href = redirectUrl;
        }
      } else {
        alert("An error occurred during checkout. Please try again.");
      }
    }
  };

  return (
    <div className="cart-page">
      <h1>Your Cart</h1>
      {cartItems.length === 0 ? (
        <p>Your cart is empty.</p>
      ) : (
        <div className="cart-items">
          {cartItems.map((item) => (
            <div key={item.id} className="cart-item">
              <div className="item-info">
                <h2>{item.name}</h2>
                <p>Price: ${(parseFloat(item.price) / 100).toFixed(2)}</p>
              </div>
              <div className="item-quantity">
                <label htmlFor={`qty-${item.id}`}>Quantity: </label>
                <input
                  id={`qty-${item.id}`}
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) =>
                    handleQuantityChange(item.id, parseInt(e.target.value, 10))
                  }
                  disabled={isIntroOffer(item)}
                />
                {isIntroOffer(item) && (
                  <span className="quantity-restriction-note">
                    Quantity cannot be changed for intro offers
                  </span>
                )}
              </div>
              <button onClick={() => removeItem(item.id)}>Remove</button>
            </div>
          ))}

          <div className="terms-agreement">
            <div
              className="checkbox-container"
              style={{ display: "flex", alignItems: "center" }}
            >
              <input
                type="checkbox"
                id="combined-checkbox"
                checked={termsAgreed}
                onChange={() => setTermsAgreed(!termsAgreed)}
                style={{ marginRight: "8px" }}
              />
              <label htmlFor="combined-checkbox">
                I agree to the{" "}
                <Link to="/terms-of-service" target="_blank">
                  Terms of Service
                </Link>
                {", "}
                and{" "}
                <Link to="/studio-policy" target="_blank">
                  Studio Policy
                </Link>
                .
              </label>
            </div>
          </div>

          <button className="checkout-button" onClick={handleStripeCheckout}>
            Proceed to Checkout
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
